import {
  ActivitiesEntryTypeEnum,
  ACTIVITIES_ENTRY,
  CODE_TO_ENTRY_TYPE,
  ExperiencesShopRequest,
  ExperiencesShopResponse,
} from "redmond";
import { call, put, select } from "redux-saga/effects";
import dayjs from "dayjs";
import * as H from "history";
import queryStringParser from "query-string";

import { actions } from "../actions";
import Logger from "../../../utils/logger";
import { actions as searchActions } from "../../search/actions";
import { PATH_HOME } from "../../../utils/paths";
import { fetchExperienceShop } from "../../../api/v0/shop/fetchExperiencesShop";
import { trackEvent } from "../../../api/v0/analytics/trackEvent";
import { IStoreState } from "../../../reducers/types";
import { getEntryType, getFromDate, getUntilDate } from "../../search/reducer";
import { getExperienceId } from "../reducer";

export interface IExperiencesShopParsedQuery {
  fromDate: Date | null;
  untilDate: Date | null;
  experienceId: string;
  index: number;
  entryType: ActivitiesEntryTypeEnum | null;
  entryTypesAll: ActivitiesEntryTypeEnum[] | null;
  lodgingId: string | null;
  staysAdvance: number | null;
}

export const parseEntryTypes = (entryType: string | string[] | null): ActivitiesEntryTypeEnum[] => {
  if (!entryType) return []; // handle null safely

  // Normalize: always work with an array of strings
  const entries = Array.isArray(entryType) ? entryType : [entryType];

  const result: ActivitiesEntryTypeEnum[] = entries.flatMap((entry) =>
    entry
      .split("_")
      .map((part) => CODE_TO_ENTRY_TYPE[part])
      .filter((mapped): mapped is ActivitiesEntryTypeEnum => mapped != null)
  );

  return result;
}

const parseQueryString = (
  history: H.History,
  isDatelessEnabled?: boolean
): IExperiencesShopParsedQuery | null => {
  const queryString = history?.location?.search || "";

  const { activityId, fromDate, untilDate, index, entryType, lodgingId, staysAdvance } =
    queryStringParser.parse(queryString);

  const hasBothDates = fromDate && untilDate;
  const hasNoDates = !fromDate && !untilDate;

  // Reject partial dates (one set, one missing) — same guard as availability saga's shouldRedirect.
  // No dates is only valid when dateless experiment is enabled.
  if (!activityId || (!hasBothDates && !hasNoDates) || (hasNoDates && !isDatelessEnabled)) {
    history.push(PATH_HOME);
    return null;
  }

  const entryTypesSplit = parseEntryTypes(entryType)

  const entryTypeLatest = entryTypesSplit.length > 0
  ? entryTypesSplit[entryTypesSplit.length - 1]
  : null;

  return {
    fromDate: fromDate ? dayjs(fromDate as string).toDate() : null,
    untilDate: untilDate ? dayjs(untilDate as string).toDate() : null,
    experienceId: activityId as string,
    index: (index ?? 0) as number,
    entryType: entryTypeLatest,
    entryTypesAll: entryTypesSplit,
    lodgingId: lodgingId ? (lodgingId as string) : null,
    staysAdvance: staysAdvance ? parseInt(staysAdvance as string, 10) : null,
  };
};

const getExistingStateVariables = (state: IStoreState) => ({
  fromDate: getFromDate(state),
  untilDate: getUntilDate(state),
  experienceId: getExperienceId(state),
  entryType: getEntryType(state),
});

 
function* getShopRequestParameters(
  fetchExperiencesShop: actions.IFetchExperiencesShop
) {
  const state: IStoreState = yield select();
  let { fromDate, untilDate, experienceId } =
    getExistingStateVariables(state);
  const { history, isDatelessEnabled } = fetchExperiencesShop;

  const parsedQueryString = parseQueryString(history, isDatelessEnabled);

  if (!parsedQueryString) {
    return undefined;
  }

  if (!fromDate && parsedQueryString.fromDate) {
    fromDate = parsedQueryString.fromDate;
    yield put(
      searchActions.setFromDate(dayjs(parsedQueryString.fromDate).toDate())
    );
  }

  if (!untilDate && parsedQueryString.untilDate) {
    untilDate = parsedQueryString.untilDate;
    yield put(
      searchActions.setUntilDate(dayjs(parsedQueryString.untilDate).toDate())
    );
  }

  if (!experienceId) {
    experienceId = { value: parsedQueryString.experienceId };
    yield put(
      actions.setSelectedExperience({
        value: parsedQueryString.experienceId,
      })
    );
  }

  if (parsedQueryString.entryType) {
    yield put(searchActions.setEntryType(parsedQueryString.entryType));
  }

  if (parsedQueryString.entryTypesAll && parsedQueryString.entryTypesAll.length > 0) {
    yield put(searchActions.setEntryTypesAll(parsedQueryString.entryTypesAll));
  }

  if (parsedQueryString.lodgingId) {
    yield put(searchActions.setLodgingId(parsedQueryString.lodgingId));
  }

  if (parsedQueryString.staysAdvance !== null) {
    yield put(searchActions.setStaysAdvance(parsedQueryString.staysAdvance));
  }

  let shopContext;
  try {
    const localStoredShopContext = localStorage.getItem(
      `shop-context-${experienceId.value}`
    );
    if (localStoredShopContext) {
      shopContext = JSON.parse(localStoredShopContext);
    }
  } catch (e) {
    Logger.debug(e);
  }

  const shopRequestData: ExperiencesShopRequest = {
    id: {
      value: experienceId.value,
    },
    // Omit dateRange for dateless requests — API returns all upcoming availability
    ...(fromDate && untilDate
      ? {
          dateRange: {
            from: dayjs(fromDate).format("YYYY-MM-DD"),
            until: dayjs(untilDate).format("YYYY-MM-DD"),
          },
        }
      : {}),
    shopContext,
  };

  return shopRequestData;
}

export function* fetchExperiencesShopSaga(
  action: actions.IFetchExperiencesShop
) {
  try {
    const requestData: ExperiencesShopRequest = yield call(
      getShopRequestParameters,
      action
    );

    const { history, isDatelessEnabled } = action;
    const parsedQueryString = parseQueryString(history, isDatelessEnabled);

    if (!parsedQueryString) {
      return;
    }

    const shopResponse: ExperiencesShopResponse = yield fetchExperienceShop(
      requestData
    );
    yield put(
      actions.setExperiencesShopDetails({
        response: shopResponse,
        activtyIndex: parsedQueryString.index,
      })
    );

    // Fire activities_entry for direct shop entry via combined search product.
    // The availability page normally fires this event on InitialSearch, but
    // product results bypass availability entirely. Fire here only when the
    // user did NOT visit the availability page in this session — detected by
    // checking that no availability-routed entry type appears in the chain.
    const AVAILABILITY_ENTRY_TYPES = new Set([
      ActivitiesEntryTypeEnum.COMBINED_SEARCH_DESTINATION,
      ActivitiesEntryTypeEnum.COMBINED_SEARCH_CATEGORY,
      ActivitiesEntryTypeEnum.COMBINED_SEARCH_ATTRACTION,
      ActivitiesEntryTypeEnum.RECENTLY_SEARCHED,
      ActivitiesEntryTypeEnum.UPCOMING_TRIP,
    ]);
    if (
      parsedQueryString.entryType === ActivitiesEntryTypeEnum.COMBINED_SEARCH_PRODUCT &&
      parsedQueryString.entryTypesAll &&
      !parsedQueryString.entryTypesAll.some((t) => AVAILABILITY_ENTRY_TYPES.has(t))
    ) {
      try {
        trackEvent({
          eventName: ACTIVITIES_ENTRY,
          properties: {
            entry_type: parsedQueryString.entryType,
            entry_types_all: parsedQueryString.entryTypesAll,
          },
          encryptedProperties: [],
        });
      } catch {
        // Non-critical — don't fail the shop flow for tracking errors
      }
    }

    // Auto-select the first available date when arriving without dates
    if (!parsedQueryString.fromDate && !parsedQueryString.untilDate) {
      const firstBookableItem = shopResponse.bookableItems?.[0];
      if (firstBookableItem?.timeSlotsByDate) {
        const availableDates = Object.keys(
          firstBookableItem.timeSlotsByDate
        ).sort();
        if (availableDates.length > 0) {
          yield put(actions.setSelectedDate(availableDates[0]));
          yield put(actions.setSelectedBookableItem(firstBookableItem));
        }
      }
    }
  } catch (e) {
    yield put(actions.setExperiencesShopDetailsCallStateFailed());
    Logger.debug(e);
  }
}
